import { useEffect, useState } from 'react';
import {
  Plus, Pencil, Circle, Disc, Disc3, Sparkles, Snowflake, Wind, Target, Medal, Bike, Waves, Check,
  Landmark, ShieldCheck, FileText,
} from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { Button, Card, CardBody, Field, Input, Modal, Switch } from '../components/ui';
import { useCreateSection, useDashboard, useSections, useUpdateSection } from '../api/queries';
import { openAuthedHtml } from '../api/client';
import { useToast } from '../lib/toast';
import type { CurrentUser, Section } from '../api/types';

const SECTION_COLORS = ['#16a34a', '#ca9a04', '#dc2626', '#a855f7', '#0ea5e9', '#3b82f6', '#8b5cf6', '#0f766e', '#db2777', '#c2410c', '#475569'];
const SECTION_ICONS = ['circle', 'disc', 'disc-3', 'sparkles', 'snowflake', 'wind', 'target', 'medal', 'bike', 'waves', 'landmark', 'shield-check'];

const ICON_MAP: Record<string, typeof Circle> = {
  circle: Circle, disc: Disc, 'disc-3': Disc3, sparkles: Sparkles, snowflake: Snowflake,
  wind: Wind, target: Target, medal: Medal, bike: Bike, waves: Waves,
  landmark: Landmark, 'shield-check': ShieldCheck,
};

function SectionIcon({ name, size = 20 }: { name: string; size?: number }) {
  const Comp = ICON_MAP[name] ?? Circle;
  return <Comp size={size} />;
}

export function SectionsScreen({ user }: { user: CurrentUser }) {
  const { data: sections = [], isLoading } = useSections();
  const { data: dashboard } = useDashboard();
  const toast = useToast();
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const memBySec: Record<string, number> = {};
  (dashboard?.sektionen ?? []).forEach((s) => { memBySec[s.id] = s.count; });

  const canManage = user.rolle === 'admin';
  const canExport = user.rolle !== 'read_only';

  return (
    <div className="page-fade">
      <PageHeader
        title="Sektionen"
        subtitle={`${sections.length} Sektionen`}
        actions={canManage ? (
          <Button variant="primary" icon={<Plus size={16} />} onClick={() => setAddOpen(true)}>
            Sektion hinzufügen
          </Button>
        ) : null}
      />

      <div className="grid-3">
        {isLoading && sections.length === 0 && [0, 1, 2, 3, 4, 5, 6].map((i) => (
          <div key={`sk-${i}`} className="skeleton" style={{ height: 168, borderRadius: 12 }} />
        ))}
        {sections.map((s) => {
          const count = memBySec[s.id] ?? 0;
          return (
            <Card key={s.id} style={{ overflow: 'hidden', opacity: s.aktiv ? 1 : 0.65 }}>
              <div style={{ height: 4, background: s.color }} />
              <CardBody>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 11,
                    background: `color-mix(in srgb, ${s.color} 16%, transparent)`,
                    color: s.color, display: 'grid', placeItems: 'center', flexShrink: 0,
                  }}>
                    <SectionIcon name={s.icon} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="t-title-3">{s.name}</div>
                    {s.founded && <div className="text-tertiary" style={{ fontSize: 12.5, marginTop: 1 }}>seit {s.founded}</div>}
                  </div>
                  {canExport && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<FileText size={14} />}
                      onClick={async () => {
                        try { await openAuthedHtml(`/sections/${s.id}/abrechnung`); }
                        catch { toast.push('Abrechnung konnte nicht geöffnet werden', 'warn'); }
                      }}
                      title="Union-Beitrag Abrechnung als PDF"
                    />
                  )}
                  {canManage && (
                    <Button variant="ghost" size="sm" icon={<Pencil size={14} />} onClick={() => setEditingSection(s)} title="Sektion bearbeiten" />
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border-subtle)' }}>
                  {[
                    ['Mitglieder', count],
                    ['Status', s.aktiv ? 'aktiv' : 'inaktiv'],
                  ].map(([l, v]) => (
                    <div key={String(l)}>
                      <div className="text-tertiary" style={{ fontSize: 11 }}>{l}</div>
                      <div className="t-num" style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em' }}>{v}</div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          );
        })}

        {canManage && (
          <button
            onClick={() => setAddOpen(true)}
            className="card"
            style={{
              border: '1.5px dashed var(--border-default)', background: 'transparent',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 10, minHeight: 200, color: 'var(--text-tertiary)', cursor: 'pointer',
            }}
          >
            <div style={{ width: 44, height: 44, borderRadius: 11, background: 'var(--bg-muted)', display: 'grid', placeItems: 'center' }}>
              <Plus size={22} />
            </div>
            <span style={{ fontWeight: 500, fontSize: 14 }}>Neue Sektion anlegen</span>
          </button>
        )}
      </div>

      <SectionFormModal mode="add" open={addOpen} onClose={() => setAddOpen(false)} />
      <SectionFormModal
        mode="edit"
        section={editingSection}
        open={!!editingSection}
        onClose={() => setEditingSection(null)}
      />
    </div>
  );
}

function SectionFormModal({
  mode, section, open, onClose,
}: {
  mode: 'add' | 'edit';
  section?: Section | null;
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateSection();
  const update = useUpdateSection();
  const toast = useToast();
  const [color, setColor] = useState('#0f766e');
  const [icon, setIcon] = useState('medal');
  const [name, setName] = useState('');
  const [beitrag, setBeitrag] = useState('50');
  const [founded, setFounded] = useState('');
  const [aktiv, setAktiv] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && section) {
      setColor(section.color);
      setIcon(section.icon);
      setName(section.name);
      setBeitrag(String(section.beitrag_jahr));
      setFounded(section.founded != null ? String(section.founded) : '');
      setAktiv(section.aktiv === 1);
    } else {
      setColor('#0f766e');
      setIcon('medal');
      setName('');
      setBeitrag('50');
      setFounded('');
      setAktiv(true);
    }
  }, [open, mode, section]);

  const submit = () => {
    if (!name.trim()) return;
    const foundedVal = founded.trim() && Number.isFinite(Number(founded)) ? Number(founded) : null;
    if (mode === 'add') {
      create.mutate(
        { name: name.trim(), color, icon, beitrag_jahr: Number(beitrag) || 0, founded: foundedVal },
        {
          onSuccess: () => { toast.push(`Sektion „${name}" angelegt`); onClose(); },
          onError: () => toast.push('Konnte Sektion nicht anlegen', 'warn'),
        },
      );
    } else if (section) {
      update.mutate(
        {
          id: section.id,
          patch: {
            name: name.trim(),
            color,
            icon,
            beitrag_jahr: Number(beitrag) || 0,
            founded: foundedVal,
            aktiv: aktiv ? 1 : 0,
          },
        },
        {
          onSuccess: () => { toast.push('Sektion gespeichert'); onClose(); },
          onError: () => toast.push('Konnte nicht speichern', 'warn'),
        },
      );
    }
  };

  const isPending = create.isPending || update.isPending;
  if (mode === 'edit' && !section) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'add' ? 'Sektion hinzufügen' : `${section?.name} bearbeiten`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" icon={<Check size={16} />} onClick={submit} disabled={isPending || !name.trim()}>
            {mode === 'add' ? 'Sektion anlegen' : 'Speichern'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: `color-mix(in srgb, ${color} 18%, transparent)`,
            color, display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
            <SectionIcon name={icon} size={26} />
          </div>
          <Field label="Name der Sektion">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Leichtathletik" autoFocus />
          </Field>
        </div>
        <Field label="Farbe">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {SECTION_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                style={{
                  width: 30, height: 30, borderRadius: 8, background: c,
                  border: color === c ? '2.5px solid var(--text-primary)' : '2.5px solid transparent',
                  boxShadow: 'inset 0 0 0 2px var(--bg-surface)',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </Field>
        <Field label="Icon">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {SECTION_ICONS.map((ic) => (
              <button
                key={ic}
                type="button"
                onClick={() => setIcon(ic)}
                style={{
                  width: 38, height: 38, borderRadius: 9,
                  border: `1px solid ${icon === ic ? 'var(--accent)' : 'var(--border-default)'}`,
                  background: icon === ic ? 'var(--accent-soft)' : 'var(--bg-surface)',
                  color: icon === ic ? 'var(--accent)' : 'var(--text-secondary)',
                  display: 'grid', placeItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <SectionIcon name={ic} size={18} />
              </button>
            ))}
          </div>
        </Field>
        <Field label="Standard-Mitgliedsbeitrag / Jahr (EUR)">
          <Input value={beitrag} onChange={(e) => setBeitrag(e.target.value)} />
        </Field>
        <Field label="Gegründet (Jahr, optional)">
          <Input value={founded} onChange={(e) => setFounded(e.target.value)} placeholder="z. B. 1998" inputMode="numeric" />
        </Field>
        {mode === 'edit' && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--bg-muted)', borderRadius: 10 }}>
            <div>
              <div style={{ fontWeight: 500, fontSize: 14 }}>Sektion aktiv</div>
              <div className="text-tertiary" style={{ fontSize: 12.5 }}>
                Inaktive Sektionen erscheinen abgeblendet, bleiben aber für Statistik & Historie erhalten.
              </div>
            </div>
            <Switch on={aktiv} onChange={setAktiv} />
          </div>
        )}
      </div>
    </Modal>
  );
}
